import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "./store";
import { useState, useCallback, useRef } from "react";

//Simple hook to get the dispatch function from the store
export const useAppDispatch = () => useDispatch<AppDispatch>();
//Simple hook to get the state from the store
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// watch changes to the size of an element
// calls `callback` with the new size
// returns a ref callback to use on the target element and the current size state
export const useResizeObserver = (
  callback: (size: { width: number; height: number }) => void,
) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const cleanupRef = useRef<(() => void) | null>(null);

  const ref = useCallback(
    (node: Element | null) => {
      // ref callbacks get called with null when they're unmounted
      // so clean up the previous observer here
      // TODO: react 19: change callback to return a cleanup function instead
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      if (!node) {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        if (entries.length === 0) {
          return;
        }
        const { width, height } = entries[0].contentRect;
        setSize({ width, height });
        callback({ width, height });
      });
      observer.observe(node);
      cleanupRef.current = () => observer.disconnect();
    },
    [callback],
  );

  return { size, ref };
};
